/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { BetslipType } from '../../core/betslip-type';
import { BetslipGeneratorContextType, IBetslip } from '../bet-generation/models';
import { BetslipSummaryService } from '../summary/services/betslip-summary.service';
import { betslipCurrentTypeSelector } from '../types/base/selectors';

@Injectable()
export default class MultiReturnsMessageService {
    constructor(
        private sitecore: Sitecore,
        private stringInterpolationPipe: StringInterpolationPipe,
        private moneyService: MoneyService,
        private oddsFormatterService: OddsFormatterService,
        private summaryService: BetslipSummaryService,
        private store: Store,
    ) {}

    getMessage$(oddsDisplayFormat: OddsDisplayFormat, isAccaBoostSelected?: boolean): Observable<string | null> {
        const hasComboBetslipTypeSelected$ = this.store
            .select(betslipCurrentTypeSelector)
            .pipe(map((selectedType) => selectedType === BetslipType.Combo));

        return hasComboBetslipTypeSelected$.pipe(
            switchMap((isCombo) => {
                if (!isCombo) {
                    return of(null);
                }

                return this.summaryService.getSummary$(BetslipGeneratorContextType.MultiReturnMessage).pipe(
                    // only selected picks are taken into consideration when evaluating whether to show this message or not
                    distinctUntilChanged((prev, next) => !isAccaBoostSelected && this.getPickCount(prev[0]) === this.getPickCount(next[0])),
                    map(([betslip, betslipSummary]) => {
                        if (this.getPickCount(betslip) < 2) {
                            return null;
                        }
                        if (betslipSummary.betCount === 0 || betslipSummary.possibleWinningsNet === null) {
                            return null;
                        }

                        if (isAccaBoostSelected) {
                            return this.stringInterpolationPipe.transform(this.sitecore.returnsMessage.messages.AccaBoostSelectedReturnsInfo, {
                                stake: this.formatCurrency(betslipSummary.totalStake!),
                                odds: this.oddsFormatterService.format(betslipSummary.totalOdds, { displayFormat: oddsDisplayFormat }),
                                winsPossible: this.formatCurrency(
                                    betslipSummary.slipSummaries[0]?.rewardTokenModifications?.accaBoost?.originalWinnings!,
                                ),
                                wins: this.formatCurrency(betslipSummary.possibleWinningsNet),
                            });
                        }

                        return this.stringInterpolationPipe.transform(this.sitecore.returnsMessage.messages.ReturnsInfo, {
                            stake: this.formatCurrency(betslipSummary.totalStake!),
                            odds: this.oddsFormatterService.format(betslipSummary.totalOdds, {
                                displayFormat: oddsDisplayFormat,
                                cutZero: true,
                                isSummary: true,
                            }),
                            wins: this.formatCurrency(betslipSummary.possibleWinningsNet),
                        });
                    }),
                );
            }),
        );
    }

    private formatCurrency(value: Decimal): string {
        return this.moneyService.transform(value, true, 2);
    }

    private getPickCount(betslip: IBetslip) {
        return betslip.slips.reduce((acc, curr) => acc + Object.keys(curr.slipPicks).length, 0);
    }
}
